import { FormControl, Grid, Typography, WithStyles } from "@material-ui/core";
import React from "react";
import { BlueButton } from "../../Buttons/BlueButton";
import { withStyles } from "@material-ui/core/styles";
import { FormTextField } from "../../FormComponents/FormTextField";
import AddressModel from "../../../../models/user/AddressModel";
import { styles } from "./SingleAddressForm.styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PermissionGuard from "../../PermissionGuard/PermissionGuard";
import { FormNativeSelect } from "../../FormComponents/FormNativeSelect";
import { selectVisibleCountries } from "../../../../selectors/countries/CountriesSelector";

export interface IAddressesFormProps extends WithStyles<typeof styles> {
    address: AddressModel
    onSave: any,
    editMode: boolean,
    handleEditMode: any,
}

const SingleAddressFormPure: React.FunctionComponent<IAddressesFormProps> = ({
                                                                                 onSave,
                                                                                 address,
                                                                                 editMode,
                                                                                 handleEditMode,
                                                                                 classes,
                                                                             }: IAddressesFormProps) => {


    const countries = useSelector(state => selectVisibleCountries(state));

    const [errors, setErrors] = React.useState([]);

    const [details, setDetails] = React.useState({
        name: address.name,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        postcode: address.postcode,
        city: address.city,
        country: address.country,
    });

    const handleChange = (event, valid) => {
        const name = event.target.name;
        const value = event.target.value;

        setDetails({
            ...details,
            [name]: value
        });

        if (valid) {
            setErrors(errors.filter(e => e !== name));
        } else {
            setErrors(errors.concat(name));
        }
    };

    const handleCountry = (event) => {
        if (event.target.value !== "") {
            setDetails({
                ...details,
                [event.target.name]: event.target.value
            });
        }
    }

    const handleSave = () => {
        if (!errors.length) {
            address.name = details.name;
            address.addressLine1 = details.addressLine1;
            address.addressLine2 = details.addressLine2;
            address.postcode = details.postcode;
            address.city = details.city;
            address.country = details.country;
            onSave(address);
        }
    };

    const {t} = useTranslation();

    return <>
        {
            editMode
                ? <>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <FormTextField id="name" required onChange={handleChange} value={details.name}
                                           label={t("general.name")} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <FormTextField id="addressLine1" required onChange={handleChange} value={details.addressLine1}
                                           label={t("userManagement.singleAddress.addressLine1")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField id="addressLine2" onChange={handleChange} value={details.addressLine2}
                                           label={t("userManagement.singleAddress.addressLine2")} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <FormTextField id="postcode" required onChange={handleChange} value={details.postcode}
                                           label={t("general.postcode")} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormTextField id="city" required onChange={handleChange} value={details.city}
                                           label={t("general.city")} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl required variant="filled">
                                <FormNativeSelect
                                    label="country"
                                    name="country"
                                    value={details.country}
                                    onChange={handleCountry}
                                    options={countries}
                                    required
                                    emptyCaption={"Select country"}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <PermissionGuard scope="users.write" disabled>
                        <BlueButton disabled={errors.length > 0} onClick={handleSave}>{t("general.save")}</BlueButton>
                    </PermissionGuard>
                </>

                : <>
                    <Typography variant={"body1"}>{details.name}</Typography>
                    <Typography variant={"body1"}>{details.addressLine1}</Typography>
                    <Typography variant={"body1"}>{details.addressLine2}</Typography>
                    <Typography variant={"body1"}>{details.postcode} {details.city}</Typography>
                    <Typography variant={"body1"}>{details.country}</Typography>
                    <PermissionGuard scope="users.write" disabled>
                        <BlueButton onClick={handleEditMode}>{t("userManagement.singleAddress.editAddress")}</BlueButton>
                    </PermissionGuard>
                </>
        }
    </>;
};

export const SingleAddressForm = withStyles(styles)(SingleAddressFormPure);
